import React from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Braces } from 'lucide-react';

interface PlaceholderMenuProps {
  variables: { key: string; description: string }[];
  /** id of the input/textarea to insert into */
  targetId: string;
  value: string;
  onChange: (value: string) => void;
}

/** Small button that lists placeholders and inserts one at the cursor. */
export function PlaceholderMenu({ variables, targetId, value, onChange }: PlaceholderMenuProps) {
  const [open, setOpen] = React.useState(false);
  if (!variables.length) return null;

  const insert = (key: string) => {
    const token = `{{${key}}}`;
    const el = document.getElementById(targetId) as HTMLInputElement | HTMLTextAreaElement | null;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    onChange(next);
    setOpen(false);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground" aria-label="Insert placeholder">
          <Braces className="h-3.5 w-3.5 mr-1" /> Insert
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-1">
        {variables.map((v) => (
          <button
            key={v.key}
            type="button"
            onClick={() => insert(v.key)}
            className="w-full text-left rounded-sm px-2 py-1.5 hover:bg-muted focus:bg-muted focus:outline-none"
          >
            <code className="text-xs font-medium">{`{{${v.key}}}`}</code>
            <div className="text-xs text-muted-foreground">{v.description}</div>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
